import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  X,
  Search,
  User,
  Loader2,
  Upload,
  FileText,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Circle,
  PenLine,
  Check,
  Phone,
  Mail,
  IdCard,
  MapPin,
  UserPlus,
  Users,
  AlertCircle,
  Calendar,
  UserCircle
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { isValidCodiceFiscale, getCodiceFiscaleError, formatCodiceFiscale } from "../utils/codiceFiscale";

const OrderCreationModal = ({ 
  isOpen, 
  onClose, 
  macroArea, 
  selectedPratica, 
  areaInfo,
  onOrderCreated 
}) => {
  // Steps: 1 = Client Selection, 2 = Attachments & Notes, 3 = Creating
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [creationStep, setCreationStep] = useState(0);
  
  // Client mode: 'search' or 'new'
  const [clientMode, setClientMode] = useState('search');
  
  // Client search state
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [searchingClients, setSearchingClients] = useState(false);
  const clientSearchRef = useRef(null);
  
  // New client form data
  const [newClientData, setNewClientData] = useState({
    nome: "",
    cognome: "",
    telefono: "",
    codice_fiscale: "",
    email: "",
    data_nascita: "",
    sesso: ""
  });
  
  // CF validation error
  const [cfError, setCfError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    telefono: "",
    codice_fiscale: "",
    casa_appartenenza: "",
    note: "",
    client_id: "",
    client_name: ""
  });
  
  // Files
  const [files, setFiles] = useState([]);
  
  // Creation steps for animation (updated to include client creation)
  const CREATION_STEPS = clientMode === 'new' ? [
    { label: "Salvataggio nuovo cliente", delay: 800 },
    { label: "Verifica dati cliente", delay: 600 },
    { label: "Creazione pratica", delay: 800 },
    { label: "Caricamento documenti", delay: 600 },
    { label: "Finalizzazione ordine", delay: 600 }
  ] : [
    { label: "Verifica dati cliente", delay: 600 },
    { label: "Creazione pratica", delay: 800 },
    { label: "Caricamento documenti", delay: 600 },
    { label: "Registrazione nel sistema", delay: 1000 },
    { label: "Finalizzazione ordine", delay: 600 }
  ];
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsCreating(false);
      setCreationStep(0);
      setClientMode('search');
      setClientSearch("");
      setClientResults([]);
      setNewClientData({
        nome: "",
        cognome: "",
        telefono: "",
        codice_fiscale: "",
        email: "",
        data_nascita: "",
        sesso: ""
      });
      setSelectedClient(null);
      setShowClientDropdown(false);
      setFormData({
        nome: "",
        cognome: "",
        telefono: "",
        codice_fiscale: "",
        casa_appartenenza: "",
        note: "",
        client_id: "",
        client_name: ""
      });
      setFiles([]);
    }
  }, [isOpen]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Client search functions
  const searchClients = async (query) => {
    if (!query || query.length < 2) {
      setClientResults([]);
      setShowClientDropdown(false);
      return;
    }
    
    setSearchingClients(true);
    try {
      const response = await axios.get(`${API}/clients/search`, {
        params: { q: query, limit: 8 },
        withCredentials: true
      });
      setClientResults(response.data);
      setShowClientDropdown(true);
    } catch (error) {
      console.error("Errore ricerca clienti:", error);
    } finally {
      setSearchingClients(false);
    }
  };
  
  const handleClientSearchChange = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    
    // Debounce search
    clearTimeout(window.clientSearchTimeout);
    window.clientSearchTimeout = setTimeout(() => {
      searchClients(value);
    }, 300);
  };
  
  const selectClient = (client) => {
    setSelectedClient(client);
    const fullName = `${client.nome || ""} ${client.cognome || ""}`.trim();
    setClientSearch(fullName);
    setShowClientDropdown(false);
    
    // Auto-fill form fields
    setFormData(prev => ({
      ...prev,
      nome: client.nome || prev.nome,
      cognome: client.cognome || prev.cognome,
      codice_fiscale: client.codice_fiscale || prev.codice_fiscale,
      telefono: client.telefono || prev.telefono,
      client_id: client.client_id,
      client_name: fullName
    }));
    
    toast.success(`Cliente selezionato: ${fullName}`);
  };
  
  const clearSelectedClient = () => {
    setSelectedClient(null);
    setClientSearch("");
    setFormData(prev => ({
      ...prev,
      nome: "",
      cognome: "",
      telefono: "",
      codice_fiscale: "",
      client_id: "",
      client_name: ""
    }));
  };
  
  // Handle new client form change with CF validation
  const handleNewClientChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'codice_fiscale') {
      const formattedCF = formatCodiceFiscale(value);
      setNewClientData(prev => ({ ...prev, [name]: formattedCF }));
      
      // Validate CF when it has 16 characters
      if (formattedCF.length === 16) {
        const error = getCodiceFiscaleError(formattedCF);
        setCfError(error);
      } else if (formattedCF.length > 0 && formattedCF.length < 16) {
        setCfError(`Inserisci ${16 - formattedCF.length} caratteri rimanenti`);
      } else {
        setCfError(null);
      }
    } else {
      setNewClientData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Switch between search and new client modes
  const switchToNewClient = () => {
    setClientMode('new');
    setSelectedClient(null);
    setClientSearch("");
    setCfError(null);
    setFormData(prev => ({
      ...prev,
      nome: "",
      cognome: "",
      telefono: "",
      codice_fiscale: "",
      client_id: "",
      client_name: ""
    }));
  };
  
  const switchToSearch = () => {
    setClientMode('search');
    setNewClientData({
      nome: "",
      cognome: "",
      telefono: "",
      codice_fiscale: "",
      email: "",
      data_nascita: "",
      sesso: ""
    });
    setCfError(null);
  };
  
  // File handling
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const totalFiles = files.length + newFiles.length;
    
    if (totalFiles > 5) {
      toast.error("Massimo 5 documenti consentiti");
      return;
    }
    
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const oversizedFiles = newFiles.filter(f => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error("File troppo grande. Massimo 5MB per documento");
      return;
    }
    
    const pdfFiles = newFiles.filter(f => f.type === "application/pdf");
    if (pdfFiles.length !== newFiles.length) {
      toast.warning("Solo file PDF consentiti");
    }
    
    setFiles(prev => [...prev, ...pdfFiles].slice(0, 5));
  };
  
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Validation
  const isStep1Valid = () => {
    if (clientMode === 'search') {
      return selectedClient && formData.nome && formData.cognome && 
             formData.telefono && formData.codice_fiscale && formData.casa_appartenenza;
    } else {
      // New client mode - validate new client form + CF validation
      const hasAllFields = newClientData.nome && newClientData.cognome && 
             newClientData.telefono && newClientData.codice_fiscale && 
             newClientData.data_nascita && newClientData.sesso &&
             formData.casa_appartenenza;
      const cfIsValid = isValidCodiceFiscale(newClientData.codice_fiscale);
      return hasAllFields && cfIsValid;
    }
  };
  
  const handleNextStep = () => {
    if (currentStep === 1) {
      // Check CF validation specifically for new client
      if (clientMode === 'new' && newClientData.codice_fiscale) {
        const cfValidationError = getCodiceFiscaleError(newClientData.codice_fiscale);
        if (cfValidationError) {
          toast.error(cfValidationError);
          setCfError(cfValidationError);
          return;
        }
      }
      
      if (!isStep1Valid()) {
        if (clientMode === 'search') {
          toast.error("Seleziona un cliente e compila tutti i campi obbligatori");
        } else {
          toast.error("Compila tutti i campi obbligatori del nuovo cliente");
        }
        return;
      }
      setCurrentStep(2);
    }
  };
  
  const handlePrevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };
  
  // Submit order
  const handleSubmit = async () => {
    if (!isStep1Valid()) {
      toast.error("Dati cliente incompleti");
      setCurrentStep(1);
      return;
    }
    
    setIsCreating(true);
    setCurrentStep(3);
    setCreationStep(0);
    
    try {
      let clientId = formData.client_id;
      let clientName = formData.client_name;
      let clientNome = formData.nome;
      let clientCognome = formData.cognome;
      let clientTelefono = formData.telefono;
      let clientCF = formData.codice_fiscale;
      
      // If new client mode, create the client first
      if (clientMode === 'new') {
        // Step 1: Create new client
        await new Promise(resolve => setTimeout(resolve, CREATION_STEPS[0].delay));
        setCreationStep(1);
        
        const newClientPayload = {
          nome: newClientData.nome.toUpperCase(),
          cognome: newClientData.cognome.toUpperCase(),
          telefono: newClientData.telefono,
          codice_fiscale: newClientData.codice_fiscale.toUpperCase(),
          email: newClientData.email || null,
          data_nascita: newClientData.data_nascita,
          sesso: newClientData.sesso
        };
        
        const clientResponse = await axios.post(`${API}/clients`, newClientPayload, {
          withCredentials: true
        });
        
        clientId = clientResponse.data.client_id;
        clientName = `${newClientPayload.nome} ${newClientPayload.cognome}`;
        clientNome = newClientPayload.nome;
        clientCognome = newClientPayload.cognome;
        clientTelefono = newClientPayload.telefono;
        clientCF = newClientPayload.codice_fiscale;
        
        toast.success(`Nuovo cliente creato: ${clientName}`);
        
        // Continue with remaining animation steps
        for (let i = 1; i < CREATION_STEPS.length; i++) {
          await new Promise(resolve => setTimeout(resolve, CREATION_STEPS[i].delay));
          setCreationStep(i + 1);
        }
      } else {
        // Animate through steps for existing client
        for (let i = 0; i < CREATION_STEPS.length; i++) {
          await new Promise(resolve => setTimeout(resolve, CREATION_STEPS[i].delay));
          setCreationStep(i + 1);
        }
      }
      
      // Create order
      const submitData = new FormData();
      submitData.append("nome", clientNome);
      submitData.append("cognome", clientCognome);
      submitData.append("telefono", clientTelefono);
      submitData.append("codice_fiscale", clientCF);
      submitData.append("casa_appartenenza", formData.casa_appartenenza);
      submitData.append("prodotto_richiesto", selectedPratica.label);
      submitData.append("macro_area", macroArea);
      if (formData.note) {
        submitData.append("note", formData.note);
      }
      if (clientId) {
        submitData.append("client_id", clientId);
        submitData.append("client_name", clientName);
      }
      
      files.forEach(file => {
        submitData.append("files", file);
      });
      
      await axios.post(`${API}/orders`, submitData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000
      });
      
      // Show success briefly
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success("Ordine creato con successo!");
      
      if (onOrderCreated) {
        onOrderCreated();
      }
      onClose();
      
    } catch (error) {
      console.error("Errore:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Errore sconosciuto";
      toast.error(`Errore: ${errorMessage}`);
      setIsCreating(false);
      setCurrentStep(2);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="order-creation-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={() => !isCreating && onClose()}
      />
      
      {/* Modal Container */}
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex-shrink-0 p-5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {areaInfo && (
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${areaInfo.color} flex items-center justify-center text-lg shadow-md`}>
                  {areaInfo.icon}
                </div>
              )}
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  Nuovo Ordine
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {selectedPratica?.label}
                </p>
              </div>
            </div>
            {!isCreating && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            )}
          </div>
          
          {/* Progress Steps */}
          {!isCreating && (
            <div className="flex items-center gap-2 mt-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                currentStep >= 1 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-slate-100 text-slate-500'
              }`}>
                <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px]">1</span>
                Cliente
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                currentStep >= 2 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
              }`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                  currentStep >= 2 ? 'bg-blue-500 text-white' : 'bg-slate-300 text-slate-600 dark:bg-slate-600 dark:text-slate-300'
                }`}>2</span>
                Allegati
              </div>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          
          {/* Step 1: Client Selection */}
          {currentStep === 1 && (
            <div className="space-y-5 animate-in fade-in duration-200" data-testid="step-1-content">
              
              {/* Mode Toggle Tabs */}
              <div className="flex rounded-xl bg-slate-100 dark:bg-slate-700 p-1">
                <button
                  type="button"
                  onClick={switchToSearch}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    clientMode === 'search'
                      ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                  data-testid="tab-search-client"
                >
                  <Users className="w-4 h-4" />
                  Cerca Cliente
                </button>
                <button
                  type="button"
                  onClick={switchToNewClient}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    clientMode === 'new'
                      ? 'bg-white dark:bg-slate-800 text-green-600 dark:text-green-400 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                  data-testid="tab-new-client"
                >
                  <UserPlus className="w-4 h-4" />
                  Nuovo Cliente
                </button>
              </div>
              
              {/* Search Client Mode */}
              {clientMode === 'search' && (
                <>
                  {/* Client Search */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Search className="w-4 h-4 text-blue-500" />
                      Cerca Cliente
                    </Label>
                    <div className="relative" ref={clientSearchRef}>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={clientSearch}
                          onChange={handleClientSearchChange}
                          placeholder="Cerca per nome, cognome o CF..."
                          className="pl-10 pr-10"
                          data-testid="modal-client-search"
                        />
                        {selectedClient && (
                          <button
                            type="button"
                            onClick={clearSelectedClient}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {searchingClients && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />
                        )}
                      </div>
                      
                      {/* Dropdown Results */}
                      {showClientDropdown && clientResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-xl max-h-52 overflow-y-auto">
                          {clientResults.map((client) => (
                            <button
                              key={client.client_id}
                              type="button"
                              onClick={() => selectClient(client)}
                              className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-0 transition-colors"
                              data-testid={`modal-client-${client.client_id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-medium text-sm">
                                  {client.nome?.charAt(0)}{client.cognome?.charAt(0)}
                                </div>
                                <div>
                                  <div className="font-medium text-slate-900 dark:text-white">
                                    {client.nome} {client.cognome}
                                  </div>
                                  <div className="text-xs text-slate-500 dark:text-slate-400">
                                    CF: {client.codice_fiscale}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {showClientDropdown && clientResults.length === 0 && clientSearch.length >= 2 && !searchingClients && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-lg p-4 text-center text-slate-500">
                          Nessun cliente trovato
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Selected Client Card */}
                  {selectedClient && (
                    <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-lg">
                          {formData.nome?.charAt(0)}{formData.cognome?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {formData.nome} {formData.cognome}
                          </p>
                          <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Cliente selezionato
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <IdCard className="w-3.5 h-3.5" />
                          <span className="truncate">{formData.codice_fiscale}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <Phone className="w-3.5 h-3.5" />
                          <span className="truncate">{formData.telefono}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Address field (shown when client selected) */}
                  {selectedClient && (
                    <div className="space-y-4 pt-2">
                      <div>
                        <Label htmlFor="casa_appartenenza" className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-orange-500" />
                          Indirizzo / Riferimento *
                        </Label>
                        <Input
                          id="casa_appartenenza"
                          name="casa_appartenenza"
                          value={formData.casa_appartenenza}
                          onChange={handleChange}
                          placeholder="Via Roma 1, 00100 Roma"
                          className="mt-1.5"
                          data-testid="modal-input-indirizzo"
                        />
                      </div>
                    </div>
                  )}
                  
                  {!selectedClient && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
                        <User className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-slate-500 dark:text-slate-400">
                        Cerca e seleziona un cliente per procedere
                      </p>
                    </div>
                  )}
                </>
              )}
              
              {/* New Client Mode */}
              {clientMode === 'new' && (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Inserisci i dati del nuovo cliente
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="new-nome" className="text-xs">Nome *</Label>
                      <Input
                        id="new-nome"
                        name="nome"
                        value={newClientData.nome}
                        onChange={handleNewClientChange}
                        placeholder="Mario"
                        className="mt-1"
                        data-testid="new-client-nome"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-cognome" className="text-xs">Cognome *</Label>
                      <Input
                        id="new-cognome"
                        name="cognome"
                        value={newClientData.cognome}
                        onChange={handleNewClientChange}
                        placeholder="Rossi"
                        className="mt-1"
                        data-testid="new-client-cognome"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="new-cf" className="text-xs flex items-center gap-1">
                      <IdCard className="w-3 h-3" />
                      Codice Fiscale *
                    </Label>
                    <Input
                      id="new-cf"
                      name="codice_fiscale"
                      value={newClientData.codice_fiscale}
                      onChange={handleNewClientChange}
                      placeholder="RSSMRA85M01H501Z"
                      maxLength={16}
                      className={`mt-1 uppercase ${
                        cfError && newClientData.codice_fiscale.length === 16 
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                          : newClientData.codice_fiscale.length === 16 && !cfError
                            ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                            : ''
                      }`}
                      data-testid="new-client-cf"
                    />
                    {/* CF validation feedback */}
                    {newClientData.codice_fiscale && (
                      <div className={`mt-1.5 flex items-center gap-1.5 text-xs ${
                        cfError ? 'text-red-500' : newClientData.codice_fiscale.length === 16 ? 'text-green-600' : 'text-slate-500'
                      }`}>
                        {cfError ? (
                          <>
                            <AlertCircle className="w-3 h-3" />
                            {cfError}
                          </>
                        ) : newClientData.codice_fiscale.length === 16 ? (
                          <>
                            <CheckCircle2 className="w-3 h-3" />
                            Codice fiscale valido
                          </>
                        ) : (
                          <span>{newClientData.codice_fiscale.length}/16 caratteri</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="new-telefono" className="text-xs flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        Telefono *
                      </Label>
                      <Input
                        id="new-telefono"
                        name="telefono"
                        value={newClientData.telefono}
                        onChange={handleNewClientChange}
                        placeholder="333 1234567"
                        className="mt-1"
                        data-testid="new-client-telefono"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-email" className="text-xs flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        Email (opz.)
                      </Label>
                      <Input
                        id="new-email"
                        name="email"
                        type="email"
                        value={newClientData.email}
                        onChange={handleNewClientChange}
                        placeholder="mario@email.it"
                        className="mt-1"
                        data-testid="new-client-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="new-data-nascita" className="text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Data di Nascita *
                      </Label>
                      <Input
                        id="new-data-nascita"
                        name="data_nascita"
                        type="date"
                        value={newClientData.data_nascita}
                        onChange={handleNewClientChange}
                        max={new Date().toISOString().split('T')[0]}
                        className="mt-1"
                        data-testid="new-client-data-nascita"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-sesso" className="text-xs flex items-center gap-1">
                        <UserCircle className="w-3 h-3" />
                        Sesso *
                      </Label>
                      <select
                        id="new-sesso"
                        name="sesso"
                        value={newClientData.sesso}
                        onChange={handleNewClientChange}
                        className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="new-client-sesso"
                      >
                        <option value="">Seleziona...</option>
                        <option value="M">Maschio</option>
                        <option value="F">Femmina</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="casa_appartenenza_new" className="text-xs flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-orange-500" />
                      Indirizzo / Riferimento *
                    </Label>
                    <Input
                      id="casa_appartenenza_new"
                      name="casa_appartenenza"
                      value={formData.casa_appartenenza}
                      onChange={handleChange}
                      placeholder="Via Roma 1, 00100 Roma"
                      className="mt-1"
                      data-testid="new-client-indirizzo"
                    />
                  </div>
                  
                  {/* Preview new client */}
                  {newClientData.nome && newClientData.cognome && (
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold">
                          {newClientData.nome?.charAt(0)?.toUpperCase()}{newClientData.cognome?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {newClientData.nome.toUpperCase()} {newClientData.cognome.toUpperCase()}
                          </p>
                          <p className="text-xs text-slate-500">Nuovo cliente</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Step 2: Attachments & Notes */}
          {currentStep === 2 && (
            <div className="space-y-5 animate-in fade-in duration-200" data-testid="step-2-content">
              {/* Summary Card */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                    clientMode === 'new' 
                      ? 'bg-gradient-to-br from-green-400 to-emerald-500' 
                      : 'bg-gradient-to-br from-blue-400 to-blue-600'
                  }`}>
                    {clientMode === 'new' 
                      ? `${newClientData.nome?.charAt(0)?.toUpperCase() || ''}${newClientData.cognome?.charAt(0)?.toUpperCase() || ''}`
                      : `${formData.nome?.charAt(0) || ''}${formData.cognome?.charAt(0) || ''}`
                    }
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 dark:text-white">
                      {clientMode === 'new' 
                        ? `${newClientData.nome?.toUpperCase() || ''} ${newClientData.cognome?.toUpperCase() || ''}`
                        : `${formData.nome} ${formData.cognome}`
                      }
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {selectedPratica?.label} - {areaInfo?.label}
                      {clientMode === 'new' && <span className="ml-2 text-green-600">(Nuovo cliente)</span>}
                    </p>
                  </div>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${areaInfo?.color} flex items-center justify-center text-sm`}>
                    {selectedPratica?.icon}
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <Label htmlFor="note">Note (opzionale)</Label>
                <Textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  placeholder="Aggiungi note o dettagli..."
                  className="mt-1.5 min-h-24"
                  data-testid="modal-input-note"
                />
              </div>
              
              {/* File Upload */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-red-500" />
                  Documenti (opzionale)
                </Label>
                <p className="text-xs text-slate-500 mb-3">Carica fino a 5 PDF (max 5MB ciascuno)</p>
                
                <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-xl p-5 text-center hover:border-blue-300 dark:hover:border-blue-500 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept="application/pdf"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="modal-pdf-upload"
                    data-testid="modal-pdf-upload"
                  />
                  <label htmlFor="modal-pdf-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 dark:text-slate-300">Clicca per caricare PDF</p>
                    <p className="text-xs text-slate-400 mt-1">{files.length}/5 documenti</p>
                  </label>
                </div>
                
                {files.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {files.map((file, index) => (
                      <div
                        key={`file-${index}-${file.name}`}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-red-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate max-w-[180px]">
                              {file.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 3: Creating Animation */}
          {currentStep === 3 && (
            <div className="py-4 animate-in fade-in duration-200" data-testid="step-3-content">
              {/* Writing Person Animation */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-28 h-28 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center">
                    <div className="relative">
                      {/* Body */}
                      <div className="w-14 h-16 bg-gradient-to-b from-blue-500 to-blue-600 rounded-t-full relative">
                        {/* Head */}
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 w-9 h-9 bg-amber-200 rounded-full">
                          <div className="absolute top-2.5 left-2 w-1.5 h-1.5 bg-slate-700 rounded-full"></div>
                          <div className="absolute top-2.5 right-2 w-1.5 h-1.5 bg-slate-700 rounded-full"></div>
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-2.5 h-1 bg-pink-300 rounded-full"></div>
                        </div>
                        {/* Writing arm */}
                        <div className="absolute top-1.5 -right-3 origin-left animate-writing">
                          <div className="w-7 h-1.5 bg-amber-200 rounded-full transform rotate-45">
                            <PenLine className="w-3.5 h-3.5 text-slate-700 absolute -right-1.5 -top-1 transform rotate-45" />
                          </div>
                        </div>
                        <div className="absolute top-1.5 -left-1.5 w-5 h-1.5 bg-amber-200 rounded-full transform -rotate-30"></div>
                      </div>
                      <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-16 h-2.5 bg-amber-700 rounded-sm"></div>
                      <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-7 h-5 bg-white rounded-sm shadow-sm border border-slate-200">
                        <div className="p-0.5 space-y-0.5">
                          <div className="w-full h-0.5 bg-slate-200 rounded animate-pulse"></div>
                          <div className="w-3/4 h-0.5 bg-slate-200 rounded animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-1 -right-1 animate-float">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-center text-slate-900 dark:text-white mb-1">
                Creazione in corso...
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-5">
                Attendi mentre prepariamo la pratica
              </p>
              
              {/* Progress Steps */}
              <div className="space-y-2">
                {CREATION_STEPS.map((step, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-3 p-2.5 rounded-lg transition-all duration-500 ${
                      creationStep > index 
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                        : creationStep === index 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 animate-pulse' 
                          : 'bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {creationStep > index ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : creationStep === index ? (
                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                      ) : (
                        <Circle className="w-4 h-4 text-slate-300 dark:text-slate-500" />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${
                      creationStep > index 
                        ? 'text-green-700 dark:text-green-400' 
                        : creationStep === index 
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-slate-400 dark:text-slate-500'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Progress bar */}
              <div className="mt-5">
                <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${(creationStep / CREATION_STEPS.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {!isCreating && (
          <div className="flex-shrink-0 p-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/50">
            {currentStep === 1 ? (
              <>
                <Button
                  variant="ghost"
                  onClick={onClose}
                  className="text-slate-500"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleNextStep}
                  disabled={!isStep1Valid()}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  data-testid="modal-next-btn"
                >
                  Continua
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={handlePrevStep}
                  className="text-slate-500"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  data-testid="modal-create-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Crea Ordine
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderCreationModal;
